import type { CSSProperties, FunctionalComponent, VNode } from 'vue'
import type { IFormFieldProps, SelectComponentMap } from './types'
import BasicInput from 'components/form/basic-input'
import SelectInput from '../select-input'
import DisplayInput from '../display-input'
import Datepicker from '../datepicker'
import HeaderSm from '../../headers/sm'
import InfoIcon from 'assets/img/svg/icons/info-circle.svg?url'
import Tooltip from 'components/tooltip'
import { computed, defineComponent, ref } from 'vue'
import Switchinput from '../switch-input'
import { condcat } from '@/utils/string'
import Icon from 'components/icon'
import RadioGroup from '../radio-group'
import Button from 'components/button'
import FormErrorMessage from '../form-error-message'
import EditableImage from 'components/editable-image'
import SelectDropDown from 'components/select-dropdown'
import { isFunction } from '@/utils/is'
import CheckboxInput from '../checkbox-input'
import { FORM_INPUT_FIELD_PROPS } from '../input/types'
import Bubbletip from 'components/bubbletip'
import ColorPicker from 'components/color-picker'
import ColorPickerV2 from 'components/color-picker-v2'
import { Grid } from 'components/grid'
import TextArea from 'components/textarea'
import FileUpload from 'components/file-upload'
import AttachmentUpload from 'components/attachment-upload'
import TextEditor from 'components/text-editor'
import RangeInput from '../range-input'
import { Select } from 'components/select'
import SearchDropDown from 'components/search-dropdown'
import type { FormError } from '../validation'
import RichSwitchInput from '../rich-switch-input'
import FileDrop from 'components/form-file-drop'

const selectComponent = (type: string, extension?: SelectComponentMap) => {
  if (extension && type in extension && extension[type]) {
    return extension[type]
  }
  switch (type) {
    case 'currency':
    case 'number':
    case 'text':
    case 'password':
    case 'value':
    case 'email':
      return BasicInput;
    case 'textarea':
    case 'text-area':
      return TextArea;
    case 'select2':
      return Select;
    case 'select':
      return SelectInput
    case 'select-dropdown':
      return SelectDropDown
    case 'search-dropdown':
      return SearchDropDown
    case 'display':
      return DisplayInput
    case 'header':
      return HeaderSm
    case 'date':
      return Datepicker
    case 'checkbox':
      return CheckboxInput
    case 'bool':
    case 'switch':
    case 'boolean':
      return Switchinput
    case 'rich-switch':
      return RichSwitchInput
    case 'radioGroup':
      return RadioGroup
    case 'button':
      return Button
    case 'image':
      return EditableImage;
    case 'file-upload':
    case 'file':
      return FileUpload;
    case 'file-drop':
      return FileDrop;
    case 'attachment':
    case 'attachment-upload':
      return AttachmentUpload;
    case 'color-picker':
      return ColorPicker;
    case 'color-picker-v2':
      return ColorPickerV2;
    case 'text-editor':
      return TextEditor;
    case 'range':
      return RangeInput;
    default:
      return (props: any) => <input {...props} />
  }
}

export const FormFieldWrapper = defineComponent<Partial<IFormFieldProps>>({
  name: 'FormFieldWrapper',
  setup(props, { slots }) {
    const node = ref<VNode>()

    const isSelect = computed(() => props.componentType === 'select' || props.type === 'select')
    const isButton = computed(() => props.componentType === 'button' || props.type === 'button')

    const labelClazz = computed(() =>
      condcat(
        [
          [!props.inlineLabel, 'block'],
          [!props.labelClass, 'text-sm font-medium text-gray-900 dark:text-white'],
          [!!props.labelClass, props.labelClass]
        ],
        ' '
      )
    )
    const wrapClazz = computed(() =>
      condcat(
        [
          [!props.inlineLabel, 'flex flex-row items-center mb-2 gap-1'],
          [props.inlineLabel, 'flex items-center']
        ],
        ' '
      )
    )

    const Label = computed(() => (
      <div style={{ userSelect: 'none' }} class={wrapClazz.value}>
        <label
          for={props.id || props.name || (typeof props.label === 'string' ? props.label : '')}
          class={labelClazz.value}
        >
          {isFunction(props.label) ? <props.label /> : props.label}
        </label>
        {props.tooltip && (
          <Tooltip text={props.tooltip}>
            <Icon src={InfoIcon} size={14} fill="#6B7280" style={{ marginLeft: '0.3333rem' }} />
          </Tooltip>
        )}
      </div>
    ))

    const LabelWithInput = computed(() => (
      <div
        style={[
          {
            width: '100%',
          },
          props.inlineLabel
            ? ({
                height: 'fit-content',
                display: 'grid',
                gridTemplateColumns: 'fit-content(100%) auto',
                gap: '1rem',
                alignItems: 'center'
              } as CSSProperties)
            : {}
        ]}
      >
        {props.label && props.labelPlacement !== 'right' && Label.value}

        {props.locked && props.lockedReason && props.lockedReason !== 'undefined' ? (
          <Tooltip text={props.lockedReason}>{slots.default ? slots.default() : ''}</Tooltip>
        ) : slots.default ? (
          slots.default()
        ) : (
          ''
        )}
        {props.label && props.labelPlacement === 'right' && Label.value}
      </div>
    ))

    const Wrapper = computed((): FunctionalComponent<any> => {
      if (props.grid)
        return (_, ctx) => {
          return <Grid {...props.grid}>{ctx.slots.default ? ctx.slots.default() : ''}</Grid>
        }

      return (innerProps, ctx) => (
        <div
          {...innerProps}
          ref={node}
          class={[
            'flex flex-col',
            !isButton.value && !isSelect.value && !props.inlineLabel && 'justify-end'
          ]}
          style={{ position: 'relative' }}
        >
          {ctx.slots.default ? ctx.slots.default() : ''}
        </div>
      )
    })

    return () => {
      return (
        <Wrapper.value>
          { props.before && <props.before/> }
          {LabelWithInput.value}
          {props.subLabel && <p class="text-gray-500 text-sm mt-2">{props.subLabel}</p>}
          { props.after && <props.after/> }
        </Wrapper.value>
      )
    }
  }
})

FormFieldWrapper.props = [
  ...FORM_INPUT_FIELD_PROPS,
  'label',
  'labelPlacement',
  'labelClass',
  'inlineLabel',
  'id',
  'name',
  'tooltip',
  'subLabel',
  'type',
  'componentType',
  'after',
  'errors',
  'errorProps',
  'locked',
  'lockedReason'
]

const FormField = defineComponent<IFormFieldProps>({
  name: 'FormField',
  setup(props) {
    const InputComponent = props.customComponent
      ? props.customComponent
      : (selectComponent(props.componentType || props.type, props.componentExtensions) as any)
    //const {id, key, name, value, locked, onChange, onBlur, onInput, type, label, ...rest } = props

    const handleInput = (event: Event, meta: any) => {
      if (props.locked) return
      if (props.onInput) {
        ;(props.onInput as any)(event, meta)
      }
    }

    const handleChange = (event: Event, m: any) => {
      if (props.locked) return
      if (props.onChange) {
        props.onChange(event, m || { keys: [] })
      }
    }

    const errors = computed((): FormError[] => {
      if (props.errors && props.errors.length > 0) return props.errors;
      if (props.fieldRef && props.fieldRef.value && props.fieldRef.value.errors && props.fieldRef.value.errors.length > 0) return props.fieldRef.value.errors;
      return [];
    })

    const elRef = ref();
    const wrapperRef = ref();

    return () => {
      return (
        <FormFieldWrapper
          label={props.label}
          labelClass={props.labelClass}
          inlineLabel={props.inlineLabel}
          subLabel={props.subLabel}
          locked={props.locked}
          lockedReason={props.lockedReason}
          grid={props.grid}
          tooltip={props.tooltip}
          type={props.type}
          componentType={props.componentType}
          labelPlacement={props.labelPlacement}
          errors={props.errors}
          errorProps={props.errorProps}
          before={props.before}
          after={props.after}
          name={props.name}
          ref={wrapperRef}
        >
          <InputComponent
            ref={elRef}
            type={props.type}
            onBlur={props.onBlur}
            onFocus={props.onFocus}
            onChange={handleChange}
            onInput={handleInput}
            numberPrecision={props.numberPrecision}
            locked={props.locked}
            disabled={!!props.locked}
            id={props.id || props.name || props.label}
            value={props.value}
            name={props.name || props.id || props.label}
            label={props.label}
            labelClass={props.labelClass}
            style={props.css}
            fieldRef={props.fieldRef}
            reportChange={props.reportChange}
            triggerBlur={props.triggerBlur}
            display={props.display}
            title={props.title}
            subtitle={props.subtitle}
            icon={props.icon}
            placeholder={props.placeholder}
            placeholderIcon={props.placeholderIcon}
            placeholderClass={props.placeholderClass}
            variant={props.variant}
            touched={props.touched}
            tooltip={props.tooltip}
            options={props.options}
            numberTweening={props.numberTweening}
            errors={props.errors}
            placement={props.placement}
            range={props.range}
            {...(props.injectedProps || {})}
          />
          <FormErrorMessage
            anchor={elRef}
            {...(props.errorProps || {})}
            errors={errors.value}
          />
          {props.bubbleTip && <Bubbletip {...props.bubbleTip} triggerRef={props.disabled || props.locked ? wrapperRef :  elRef} />}
        </FormFieldWrapper>
      )
    }
  }
})

FormField.props = [
  ...FORM_INPUT_FIELD_PROPS,
  'label',
  'subLabel',
  'inlineLabel',
  'labelPlacement',
  'type',
  'onInput',
  'onChange',
  'onBlur',
  'locked',
  'value',
  'name',
  'fieldRef',
  'display',
  'placeholder',
  'placeholderIcon',
  'placeholderClass',
  'title',
  'subtitle',
  'subtitleClass',
  'options',
  'tooltip',
  'bubbleTip',
  'touched',
  'numberTweening',
  'numberPrecision',
  'icon',
  'customComponent',
  'componentType',
  'variant',
  'css',
  'reportChange',
  'triggerBlur',
  'after',
  'injectedProps',
  'validators',
  'errors',
  'errorProps',
  'labelClass',
  'placement',
  'componentExtensions'
]

export default FormField
